<template>
  <div class="configPayrollStewardVue">
    <div class="col-md-10 col-sm-9 u-pad-all-25 c-common-box-shadow u-bg-color-fff">
      <h3 class="u-cmn-botmargin">Payroll Steward</h3>
      <p class="o-blue-regular-text">
        A Payroll Steward <strong>reviews, approves and releases</strong> payroll for this
        local, across every job — not just the ones they are on. It is deliberately a
        different person from the steward who enters the hours.
      </p>

      <p v-if="notice" class="cps__notice" data-testid="cps-notice">
        <i class="fa fa-info-circle" aria-hidden="true"></i> {{ notice }}
      </p>
      <p v-if="error" class="cps__error" data-testid="cps-error">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> {{ error }}
      </p>

      <div v-if="loading" class="cps__loading" data-testid="cps-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading…
      </div>

      <template v-else>
        <!-- SEAT COUNT — the number they are about to change, always visible -->
        <div class="cps__seats" data-testid="cps-seat-count">
          <span class="cps__seats-n">{{ seatCount }}</span>
          <span class="cps__seats-l">{{ seatCount === 1 ? 'seat' : 'seats' }} at this local</span>
        </div>

        <!-- THE COST WARNING, BEFORE the add is committed. A CA must not meet the
             charge for the first time on an invoice. -->
        <div v-if="pending" class="cps__confirm" data-testid="cps-cost-warning">
          <p>
            <strong>Adding a Payroll Steward adds a billable seat.</strong>
          </p>
          <p>
            {{ pending.first_name }} {{ pending.last_name }} would take this local from
            <strong>{{ seatCount }}</strong> to <strong>{{ seatCount + 1 }}</strong>
            {{ seatCount + 1 === 1 ? 'seat' : 'seats' }}. Seats are counted per local — someone
            holding payroll at two locals is two seats, billed to each.
          </p>
          <button class="c-btn c-btn-primary" data-testid="cps-confirm-add"
                  :disabled="saving" @click="commitAdd()">
            {{ saving ? 'Adding…' : 'Add the seat' }}
          </button>
          <a href="javascript: void(0)" class="cps__cancel" data-testid="cps-cancel-add"
             @click="pending = null">Cancel</a>
        </div>

        <!-- ADD — mirrors the group-management search a CA already knows -->
        <div v-if="canEdit && !pending" class="cps__add">
          <input
            type="text"
            class="form-control cps__search"
            placeholder="Search people to add…"
            v-model="term"
            data-testid="cps-search"
            @input="search()"
          />
          <ul v-if="results.length" class="cps__results" data-testid="cps-results">
            <li v-for="r in results" :key="r.employee_id || r.id">
              <a href="javascript: void(0)" @click="askToAdd(r)">
                {{ r.first_name }} {{ r.last_name }}
                <span class="cps__email">{{ r.email }}</span>
              </a>
            </li>
          </ul>
          <p v-else-if="term && searched" class="cps__none">No matching people.</p>
        </div>

        <table class="table cps__table" data-testid="cps-members">
          <thead>
            <tr><th>Payroll Steward</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="m in members" :key="m.employee_id" :data-testid="'cps-member-' + m.employee_id">
              <td>
                {{ m.first_name }} {{ m.last_name }}
                <span class="cps__email">{{ m.email }}</span>
              </td>
              <td class="text-right">
                <a v-if="canEdit" href="javascript: void(0)" class="cps__remove"
                   :data-testid="'cps-remove-' + m.employee_id" @click="remove(m)">Remove</a>
              </td>
            </tr>
            <tr v-if="!members.length">
              <td colspan="2" class="cps__none">
                No Payroll Stewards yet. Until someone holds this, nobody can approve a
                payroll run at this local.
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>

<script>
// #1797 — Config -> Payroll Steward.
//
// Mirrors the group-management interaction Client Admins already know (search a
// person, add them, remove them) over the existing DriveGroup primitive, rather
// than inventing a new mechanism. The Payroll-Steward-specific parts are the SEAT
// COUNT and the COST WARNING.
//
// The warning fires BEFORE the add is committed — a CA must not discover the charge
// on an invoice. `pending` holds the chosen person while the warning is shown; the
// POST only happens on explicit confirmation.
//
// No billing here. `payroll_seat_periods`, proration and invoices are cs3cfo's and
// out of scope: this states the cost, it does not levy it.
//
// Nothing on this screen touches Google. Membership is a CallSteward fact; the
// service account remains the only Google-side principal (ed, 2026-08-27).
import service from "../../services.js";
import { mapGetters } from "vuex";

export default {
  name: "ConfigPayrollSteward",
  props: ["empDetails"],
  data() {
    return {
      members: [],
      seatCount: 0,
      canEdit: false,
      term: "",
      results: [],
      searched: false,
      pending: null,
      loading: true,
      saving: false,
      notice: "",
      error: "",
    };
  },
  computed: {
    ...mapGetters({
      userToken: "userToken",
      userRole: "userRole",
      userClientID: "userClientID",
    }),
  },
  created() {
    this.load();
  },
  methods: {
    headers() {
      return {
        Authorization: this.userToken,
        "cs3-context-role": this.userRole,
        "cs3-context-clientId": this.userClientID,
      };
    },
    apiError(e, fallback) {
      const d = e && e.response ? e.response.data : null;
      const msg = d && (d.status_message || d.message);
      return typeof msg === "string" && msg ? msg : fallback;
    },
    load() {
      this.loading = true;
      this.error = "";
      service
        .get("payroll-steward", this.headers())
        .then((res) => {
          const r = (res && res.data && res.data.result) || {};
          this.members = r.members || [];
          // From the server, not from members.length: the seat count is a billing
          // fact and must come from the same place the charge would.
          this.seatCount = typeof r.seat_count === "number" ? r.seat_count : this.members.length;
          this.canEdit = !!r.can_edit;
        })
        .catch((e) => {
          this.members = [];
          this.error = this.apiError(e, "Could not load the Payroll Stewards.");
        })
        .finally(() => { this.loading = false; });
    },
    search() {
      const term = (this.term || "").trim();
      this.searched = false;
      if (term.length < 2) { this.results = []; return; }
      const h = this.headers();
      // The header name is `searchEmployee`, not `search`.
      // EmployeeController@searchEmployee reads $request->header("searchEmployee")
      // and 400s without it, so every lookup fell into .catch() and the CA saw
      // "No matching people." for every term — the add path was unreachable.
      // Same call shape as drive/DriveGroupManager.vue, which this screen mirrors.
      h.searchEmployee = term;
      service
        .get("employee/searchEmployee", h)
        .then((res) => {
          const rows = (res && res.data && res.data.result) || [];
          const held = this.members.map((m) => m.employee_id);
          // Never offer someone who already holds a seat — the server refuses it,
          // and offering it invites a CA to think an add failed.
          this.results = rows.filter((r) => held.indexOf(r.employee_id || r.id) === -1).slice(0, 10);
          this.searched = true;
        })
        .catch(() => { this.results = []; this.searched = true; });
    },
    // Shows the cost warning. Does NOT add.
    askToAdd(person) {
      this.notice = "";
      this.error = "";
      this.pending = person;
      this.results = [];
      this.term = "";
    },
    commitAdd() {
      if (!this.pending) return;
      const person = this.pending;
      this.saving = true;
      service
        .post("payroll-steward/members",
              { employee_id: person.employee_id || person.id },
              this.headers())
        .then(() => {
          this.notice = person.first_name + " " + person.last_name + " is now a Payroll Steward.";
          this.pending = null;
          this.load();
        })
        .catch((e) => { this.error = this.apiError(e, "Could not add the Payroll Steward."); })
        .finally(() => { this.saving = false; });
    },
    remove(m) {
      this.notice = "";
      this.error = "";
      service
        .del("payroll-steward/members/" + encodeURIComponent(m.employee_id), this.headers())
        .then(() => {
          this.notice = m.first_name + " " + m.last_name + " is no longer a Payroll Steward.";
          this.load();
        })
        .catch((e) => { this.error = this.apiError(e, "Could not remove the Payroll Steward."); });
    },
  },
};
</script>

<style scoped>
.cps__notice, .cps__error { padding: 8px 0; }
.cps__error { color: #c0392b; }
.cps__seats { margin: 14px 0; }
.cps__seats-n { font-size: 26px; font-weight: 700; margin-right: 6px; }
.cps__seats-l { color: #666; }
.cps__confirm {
  background: #fff8e1;
  border-left: 4px solid #fb8b03;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.cps__cancel { margin-left: 12px; font-size: 13px; }
.cps__add { margin-bottom: 14px; }
.cps__search { max-width: 340px; }
.cps__results { list-style: none; padding: 0; margin: 6px 0; max-width: 340px; border: 1px solid #e0e0e0; }
.cps__results li { padding: 6px 10px; border-bottom: 1px solid #f0f0f0; }
.cps__email { color: #888; font-size: 12px; margin-left: 8px; }
.cps__none { color: #888; }
.cps__remove { color: #c0392b; font-size: 13px; }
</style>
